import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IIssue, IProfession } from '@d-orca/shared/trusstor';
import { TradeIcon } from '@components/TradeIcon/TradeIcon';
import { tradeLabel } from '@utils/tradeLabel';
import { groupProfessionsByTradeGroup, professionOptionLabel } from '@d-orca/shared/client/utils';
import { SelectButton } from './SelectButton';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './ProfessionSelector.module.css';

interface IProfessionSelectorProps {
  value: IIssue['profession'] | null;
  onChange: (v: IIssue['profession'] | null) => void;
  /** Offer the "—" (no profession) row. Off when the stored value can't be cleared upstream. */
  clearable?: boolean;
  options: readonly IProfession[];
  compact?: boolean;
  selectStyle?: React.CSSProperties;
}

type ProfMeta = {
  _id: string;
  tradeId?: string;
  tradeGroup?: string;
  backgroundColor?: string;
  contractor?: string;
  specialty?: string;
};

const meta = (p: IProfession | IIssue['profession']): ProfMeta =>
  p as unknown as ProfMeta;

export function ProfessionSelector({ value, onChange, options, clearable = true }: IProfessionSelectorProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = value ? meta(value) : null;
  const displayText = selected ? professionOptionLabel(t, selected) : null;

  // Group options by tradeGroup, sort group names alphabetically, sort
  // professions within each group by trade label (shared with the Scheduler
  // assignee picker via `groupProfessionsByTradeGroup`).
  const grouped = useMemo(
    () => groupProfessionsByTradeGroup(options, (p) => meta(p), t),
    [options, t],
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={containerRef} className={styles.container}>
      <SelectButton
        value={displayText}
        placeholder={t('issues.field.professionPlaceholder')}
        onClick={() => setOpen((v) => !v)}
      />

      {open && (
        <div className={styles.dropdown} role="listbox">
          <ScrollArea viewportClassName={styles.dropdownBody}>
            {clearable && (
              <button
                type="button"
                className={styles.clearItem}
                onClick={() => { onChange(null); setOpen(false); }}
              >
                —
              </button>
            )}
            {grouped.map(({ group, groupLabel, items }) => {
              return (
                <div key={group} className={styles.group}>
                  <div className={styles.groupHeader}>{groupLabel.toUpperCase()}</div>
                  {items.map((p) => {
                    const m = meta(p);
                    const tradeName = tradeLabel(t, m.tradeId);
                    const sub = m.contractor ?? m.specialty ?? '';
                    return (
                      <button
                        key={m._id}
                        type="button"
                        role="option"
                        className={styles.profRow}
                        onClick={() => { onChange(p as IIssue['profession']); setOpen(false); }}
                      >
                        <span
                          className={styles.profPill}
                          style={m.backgroundColor ? { background: m.backgroundColor } : undefined}
                        >
                          {m.tradeId && (
                            <span className={styles.profPillIcon}>
                              <TradeIcon tradeId={m.tradeId} size={11} />
                            </span>
                          )}
                          <span className={styles.profPillLabel}>{tradeName}</span>
                        </span>
                        {sub && <span className={styles.profSub}>{sub}</span>}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
